import { useState, useEffect } from 'react';
import { Loader2, Save, ShieldAlert, Building2, Globe } from 'lucide-react';
import { Card } from '@/components/ui';
import { SimpleSelect } from '@/components/ui/form-wrappers';
import { useI18n } from '@/i18n';
import { toast } from 'sonner';
import { useIsSystemAdmin } from '@/components/SystemAdminGuard';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import {
  usePlatformDataGovernance,
  useUpdatePlatformDataGovernance,
  useTenantDataGovernance,
  useUpdateTenantDataGovernance,
  type DataGovernanceConfig,
  type DetectionStrategyConfig,
} from '@/hooks/use-guardrails';

const DATA_TYPES = ['pii', 'code_danger', 'credential', 'violence', 'hate'] as const;

const ACTION_OPTS = [
  { value: 'off', label: 'Off' },
  { value: 'log_only', label: 'Log' },
  { value: 'block', label: 'Block' },
];

const STRATEGY_OPTS = [
  { value: 'patterns', label: 'Patterns' },
  { value: 'ai', label: 'AI' },
  { value: 'contextual_analysis', label: 'Contextual Analysis (coming soon)' },
];

export function DataGovernancePage() {
  const { t } = useI18n();
  const isSystemAdmin = useIsSystemAdmin();
  // ISS-478: permiso granular del módulo CONFIGURACIÓN (Module:config) —
  // la sección de tenant se muestra con view y los controles de edición con
  // admin (el PUT del backend es fail-closed).
  const dgvView = useModuleAccess('config', 'view');
  const dgvAdmin = useModuleAccess('config', 'admin');

  const { data: platformDgv, isLoading: loadingPlat } = usePlatformDataGovernance();
  const updatePlatDgv = useUpdatePlatformDataGovernance();
  const { data: tenantDgv, isLoading: loadingTen } = useTenantDataGovernance();
  const updateTenDgv = useUpdateTenantDataGovernance();

  const [platCfg, setPlatCfg] = useState<DataGovernanceConfig | null>(null);
  const [tenCfg, setTenCfg] = useState<DataGovernanceConfig | null>(null);
  const [platDirty, setPlatDirty] = useState(false);
  const [tenDirty, setTenDirty] = useState(false);

  const FIELD_LABELS: Record<string, string> = {
    pii: t('dataGovernance.fields.pii') || 'PII Detection',
    code_danger: t('dataGovernance.fields.code_danger') || 'Dangerous Code',
    credential: t('dataGovernance.fields.credential') || 'Credentials',
    violence: t('dataGovernance.fields.violence') || 'Violence',
    hate: t('dataGovernance.fields.hate') || 'Hate Speech',
  };

  useEffect(() => { if (platformDgv) { setPlatCfg(platformDgv); setPlatDirty(false); } }, [platformDgv]);
  useEffect(() => { if (tenantDgv) { setTenCfg(tenantDgv); setTenDirty(false); } }, [tenantDgv]);
  useEffect(() => {
    if (platformDgv && platCfg) setPlatDirty(JSON.stringify(platformDgv) !== JSON.stringify(platCfg));
  }, [platformDgv, platCfg]);
  useEffect(() => {
    if (tenantDgv && tenCfg) setTenDirty(JSON.stringify(tenantDgv) !== JSON.stringify(tenCfg));
  }, [tenantDgv, tenCfg]);

  const updateField = (
    cfg: DataGovernanceConfig,
    setCfg: (c: DataGovernanceConfig) => void,
    dataType: string,
    key: 'action' | 'strategy',
    value: string,
  ) => {
    setCfg({
      ...cfg,
      [dataType]: { ...cfg[dataType as keyof DataGovernanceConfig], [key]: value },
    });
  };

  const savePlat = async () => {
    if (!platCfg) return;
    try { await updatePlatDgv.mutateAsync(platCfg); setPlatDirty(false); toast.success('Platform data governance saved'); }
    catch { toast.error('Failed to save'); }
  };

  const saveTen = async () => {
    if (!tenCfg) return;
    try { await updateTenDgv.mutateAsync(tenCfg); setTenDirty(false); toast.success('Tenant data governance saved'); }
    catch { toast.error('Failed to save'); }
  };

  const renderTypeRow = (
    cfg: DataGovernanceConfig,
    setCfg: (c: DataGovernanceConfig) => void,
    dataType: string,
    readOnly = false,
  ) => {
    const dt = dataType as keyof DataGovernanceConfig;
    const typeCfg = cfg[dt] as DetectionStrategyConfig;
    return (
      <div key={dataType} className="flex items-center justify-between gap-4">
            <span className="w-36 text-sm text-gray-700 dark:text-gray-300">{FIELD_LABELS[dataType]}</span>
            <div className="flex items-center gap-2">
              <SimpleSelect
                value={typeCfg.action}
                disabled={readOnly}
                options={ACTION_OPTS}
                onValueChange={(val) => updateField(cfg, setCfg, dataType, 'action', val)}
                className="w-28"
              />
              <SimpleSelect
                value={typeCfg.strategy}
                disabled={readOnly}
                options={STRATEGY_OPTS}
                onValueChange={(val) => updateField(cfg, setCfg, dataType, 'strategy', val)}
                className="w-40"
              />
            </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <ShieldAlert className="w-6 h-6 text-emerald-500" />
          {t('dataGovernance.title') || 'Data Governance'}
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          {t('dataGovernance.description') || 'Data type handling policies.'}
        </p>
      </div>

      <div className="max-w-3xl space-y-6">
        {isSystemAdmin && platCfg && (
          <Card className="p-6 border-emerald-200 dark:border-emerald-800">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-emerald-500" />
                <div>
                  <h3 className="font-semibold">{t('dataGovernance.platform') || 'Platform Policies'}</h3>
                  <p className="text-xs text-muted-foreground">
                    {t('dataGovernance.platformDescription') || 'Defaults for all tenants.'}
                  </p>
                </div>
              </div>
              <button onClick={savePlat} disabled={!platDirty || updatePlatDgv.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                {updatePlatDgv.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save
              </button>
            </div>
            {loadingPlat ? (
              <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-4 px-1 text-xs text-muted-foreground font-medium">
                  <span className="w-36">{t('dataGovernance.dataType') || 'Data Type'}</span>
                  <div className="flex items-center gap-2">
                    <span className="w-28 text-center">{t('dataGovernance.action') || 'Action'}</span>
                    <span className="w-40 text-center">{t('dataGovernance.strategy') || 'Strategy'}</span>
                  </div>
                </div>
                {DATA_TYPES.map((dt) => renderTypeRow(platCfg!, setPlatCfg, dt))}
              </div>
            )}
          </Card>
        )}

        {dgvView && tenCfg && (
          <Card className="p-6 border-emerald-200 dark:border-emerald-800">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-500" />
                <div>
                  <h3 className="font-semibold">{t('dataGovernance.tenant') || 'Tenant Policies'}</h3>
                  <p className="text-xs text-muted-foreground">
                    {t('dataGovernance.tenantDescription') || 'Can only harden platform defaults.'}
                  </p>
                </div>
              </div>
              <button onClick={saveTen} disabled={!tenDirty || updateTenDgv.isPending || !dgvAdmin}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                {updateTenDgv.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save
              </button>
            </div>
            {loadingTen ? (
              <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-4 px-1 text-xs text-muted-foreground font-medium">
                  <span className="w-36">{t('dataGovernance.dataType') || 'Data Type'}</span>
                  <div className="flex items-center gap-2">
                    <span className="w-28 text-center">{t('dataGovernance.action') || 'Action'}</span>
                    <span className="w-40 text-center">{t('dataGovernance.strategy') || 'Strategy'}</span>
                  </div>
                </div>
                {DATA_TYPES.map((dt) => renderTypeRow(tenCfg!, setTenCfg, dt, !dgvAdmin))}
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
